import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Select, Switch, Button, message, Spin } from 'antd';
import { PlusOutlined, MinusCircleOutlined, UploadOutlined, FilePdfOutlined } from '@ant-design/icons';
import productService from '../../services/productService';
import { uploadFile, UPLOAD_FOLDERS } from '../../services/uploadService';

const { Option } = Select;

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft', color: 'text-gray-500' },
  { value: 'ACTIVE', label: 'Active', color: 'text-green-600' },
  { value: 'INACTIVE', label: 'Inactive', color: 'text-red-500' },
];

const SECURITY_TYPE_OPTIONS = [
  { value: 'equity', label: 'Equity' },
  { value: 'ccps', label: 'CCPS' },
];

export default function ProductDrawer({
  open,
  onClose,
  product,
  onSuccess,
  nextPreferenceIndex,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [investorNoteFile, setInvestorNoteFile] = useState(null);

  const isEdit = !!product;

  useEffect(() => {
    if (open) {
      fetchIndustries();
      setLogoFile(null);
      setInvestorNoteFile(null);
      if (product) {
        form.setFieldsValue({
          name: product.name,
          short_name: product.short_name,
          industry_id: product.industry_id,
          security_type: product.security_type,
          isin_numbers: product.isin_numbers?.length ? product.isin_numbers : [''],
          status: product.status || 'DRAFT',
          is_visible_partner: product.is_visible_partner ?? true,
          is_visible_investor: product.is_visible_investor ?? true,
          include_in_price_list: product.include_in_price_list ?? false,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: 'DRAFT',
          is_visible_partner: true,
          is_visible_investor: true,
          include_in_price_list: false,
          isin_numbers: [''],
          security_type: 'equity',
        });
      }
    }
  }, [open, product, form]);

  const fetchIndustries = async () => {
    setLoadingIndustries(true);
    try {
      const result = await productService.getIndustries();
      if (result.success && Array.isArray(result.data)) {
        setIndustries(result.data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    } finally {
      setLoadingIndustries(false);
    }
  };

  const handleSubmit = async (values) => {
    const isinNumbers = (values.isin_numbers || []).filter((v) => v && v.trim());

    let logoUrl = isEdit ? product.logo_url : null;
    let investorNoteUrl = isEdit ? product.investor_note_url : null;

    // Create: logo is required; investor note is optional
    if (!isEdit && !logoFile) {
      message.error('Please upload a product logo');
      return;
    }

    // 1) Upload files first; only proceed if uploads succeed (or no new file in edit)
    const needLogoUpload = !!logoFile;
    const needNoteUpload = !!investorNoteFile;

    if (needLogoUpload || needNoteUpload) {
      setUploading(true);
      try {
        if (needLogoUpload) {
          const result = await uploadFile(logoFile, UPLOAD_FOLDERS.LOGOS);
          if (!result.success) {
            message.error(result.message || 'Logo upload failed');
            setUploading(false);
            return;
          }
          logoUrl = result.url;
        }
        if (needNoteUpload) {
          const result = await uploadFile(investorNoteFile, UPLOAD_FOLDERS.INVESTOR_NOTE);
          if (!result.success) {
            message.error(result.message || 'Investor note upload failed');
            setUploading(false);
            return;
          }
          investorNoteUrl = result.url;
        }
      } catch (err) {
        message.error(err?.message || 'Upload failed');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!logoUrl) {
      message.error('Please upload a product logo');
      return;
    }

    const payload = {
      name: values.name,
      short_name: values.short_name,
      industry_id: values.industry_id,
      security_type: values.security_type,
      isin_numbers: isinNumbers,
      logo_url: logoUrl,
      ...(investorNoteUrl ? { investor_note_url: investorNoteUrl } : {}),
      status: values.status,
      is_visible_partner: values.is_visible_partner,
      is_visible_investor: values.is_visible_investor,
      include_in_price_list: values.include_in_price_list,
    };
    if (!isEdit) {
      payload.product_type_id = '1';
    }

    setLoading(true);
    try {
      if (isEdit) {
        const result = await productService.updateProduct(product.id, payload);
        if (result.success) {
          message.success('Product updated successfully');
          onSuccess?.();
          onClose();
        } else {
          message.error(result.message || 'Failed to update product');
        }
      } else {
        payload.preference_index = nextPreferenceIndex;
        const result = await productService.createProduct(payload);
        if (result.success) {
          message.success('Product created successfully');
          onSuccess?.();
          onClose();
        } else {
          message.error(result.message || 'Failed to create product');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'An error occurred';
      message.error(msg);
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSubmitting = loading || uploading;
  const submitLabel = uploading ? 'Uploading…' : loading ? 'Saving…' : isEdit ? 'Update' : 'Create';

  return (
    <Drawer
      title={isEdit ? 'Edit Product' : 'Add New Product'}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={isSubmitting}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <Spin spinning={loadingIndustries}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark
        >
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input placeholder="e.g., National Stock Exchange (NSE)" />
          </Form.Item>

          <Form.Item
            label="Short Name"
            name="short_name"
            rules={[{ required: true, message: 'Please enter short name' }]}
          >
            <Input placeholder="e.g., NSE" maxLength={20} />
          </Form.Item>

          <Form.Item
            label="Industry"
            name="industry_id"
            rules={[{ required: true, message: 'Please select industry' }]}
          >
            <Select
              placeholder="Select industry"
              showSearch
              optionFilterProp="children"
              loading={loadingIndustries}
            >
              {industries.map((ind) => (
                <Option key={ind.id} value={ind.id}>
                  {ind.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Security Type"
            name="security_type"
            rules={[{ required: true, message: 'Please select security type' }]}
          >
            <Select placeholder="Select security type">
              {SECURITY_TYPE_OPTIONS.map(({ value, label }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="ISIN Numbers"
            required
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || value.length === 0 || !value.some((v) => v && v.trim())) {
                    return Promise.reject('Please add at least one ISIN number');
                  }
                },
              },
            ]}
          >
            <Form.List name="isin_numbers">
              {(fields, { add, remove }) => (
                <div className="space-y-2">
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Form.Item
                        {...restField}
                        name={name}
                        className="mb-0 flex-1"
                        rules={[
                          { required: true, message: 'ISIN is required' },
                          {
                            pattern: /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/,
                            message: 'Invalid ISIN format',
                          },
                        ]}
                      >
                        <Input placeholder="e.g., INE775A01035" className="uppercase" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          className="text-red-500 cursor-pointer text-lg"
                          onClick={() => remove(name)}
                        />
                      )}
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className="mt-2"
                  >
                    Add ISIN
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          {/* Product Logo - upload */}
          <Form.Item
            label="Product Logo"
            required
            help={isEdit && product?.logo_url && !logoFile ? 'Current logo in use. Choose a file to replace.' : undefined}
          >
            <div className="flex items-center gap-3">
              {isEdit && product?.logo_url && !logoFile && (
                <div className="w-12 h-12 rounded border border-gray-200 overflow-hidden bg-gray-50 shrink-0">
                  <img src={product.logo_url} alt="Current logo" className="w-full h-full object-cover" />
                </div>
              )}
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <span className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded cursor-pointer hover:border-green-400 hover:bg-gray-50 text-sm">
                  <UploadOutlined />
                  {logoFile ? logoFile.name : isEdit && product?.logo_url ? 'Replace logo' : 'Choose logo image'}
                </span>
              </label>
            </div>
            {!isEdit && !logoFile && (
              <div className="text-xs text-red-500 mt-1">Required for new product</div>
            )}
          </Form.Item>

          {/* Investor Note - upload (optional) */}
          <Form.Item
            label="Investor Note"
            extra="Optional"
            help={isEdit && product?.investor_note_url && !investorNoteFile ? 'Current document in use. Choose a file to replace.' : undefined}
          >
            <div className="flex items-center gap-3">
              {isEdit && product?.investor_note_url && !investorNoteFile && (
                <div className="w-10 h-10 rounded border border-gray-200 flex items-center justify-center bg-gray-50 shrink-0">
                  <FilePdfOutlined className="text-gray-400 text-xl" />
                </div>
              )}
              <label className="flex-1">
                <input
                  type="file"
                  accept=".pdf,application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => setInvestorNoteFile(e.target.files?.[0] || null)}
                />
                <span className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded cursor-pointer hover:border-green-400 hover:bg-gray-50 text-sm">
                  <UploadOutlined />
                  {investorNoteFile ? investorNoteFile.name : isEdit && product?.investor_note_url ? 'Replace document' : 'Choose investor note (PDF/image)'}
                </span>
              </label>
            </div>
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              {STATUS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  <span className={opt.color}>{opt.label}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Visible to Partner"
              name="is_visible_partner"
              valuePropName="checked"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>

            <Form.Item
              label="Visible to Investor"
              name="is_visible_investor"
              valuePropName="checked"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>

          <Form.Item
            label="Include in price list deck"
            name="include_in_price_list"
            valuePropName="checked"
            extra="When enabled, this product appears on the daily unlisted rates deck (partner pricing)."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
}
